import React from 'react';
import { Card, Spin, Typography } from 'antd';
import EmptyStateFolderIcon from '@/shared/assets/EmptyStateFolderIcon';

const ANALYTICS_CARD_STYLE = {
    borderRadius: 12,
    background: '#FFFFFF',
    border: '1px solid #E5E7EB',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    height: '100%',
};

const TITLE_STYLE = {
    fontSize: 18,
    fontWeight: 700,
    color: '#1A1A1A',
    lineHeight: 1.3,
    display: 'block',
    whiteSpace: 'normal',
};

const HEADER_ROW_STYLE = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
};

const EMPTY_CONTENT_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '0 24px',
};

/**
 * Shared light-theme card wrapper for analytics charts.
 *
 * Handles three rendering states in one place so individual chart cards
 * don't each reimplement the loading/empty pattern:
 *   - loading -> centered spinner
 *   - isEmpty -> shared folder icon + friendly copy
 *   - otherwise -> renders children inside a fixed-height frame
 *
 * The title is rendered as the first child of the card body (instead of
 * via the Card `title` prop) so it can wrap across multiple lines in
 * narrow grid columns — `Ant`'s built-in card head forces `nowrap` and
 * truncates with an ellipsis, which is the wrong behavior for our
 * sometimes-narrow chart titles like "Task Distribution".
 *
 * The fixed `chartHeight` keeps the page layout stable across the three
 * states (no jump when data arrives) and matches the heights of the
 * sibling chart cards in the dashboard grid.
 */
const AnalyticsChartCard = ({
    title,
    loading = false,
    isEmpty = false,
    emptyTitle = 'No data yet',
    emptyDescription = "Once data is available, it'll appear here.",
    chartHeight = 320,
    extra = null,
    children,
}) => {
    return (
        <Card
            className="fs-unmask"
            bordered={false}
            style={ANALYTICS_CARD_STYLE}
            styles={{ body: { padding: 20 } }}
        >
            <div style={HEADER_ROW_STYLE}>
                <Typography.Text style={TITLE_STYLE}>{title}</Typography.Text>
                {extra}
            </div>
            <div
                style={{
                    height: chartHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {loading ? (
                    <Spin size="large" />
                ) : isEmpty ? (
                    <div style={EMPTY_CONTENT_STYLE}>
                        <EmptyStateFolderIcon width={140} height={86} />
                        <Typography.Text
                            style={{ marginTop: 16, fontSize: 16, fontWeight: 600, color: '#1A1A1A' }}
                        >
                            {emptyTitle}
                        </Typography.Text>
                        <Typography.Text
                            style={{ marginTop: 6, fontSize: 14, color: '#6B7280', maxWidth: 360 }}
                        >
                            {emptyDescription}
                        </Typography.Text>
                    </div>
                ) : (
                    <div style={{ width: '100%', height: '100%' }}>{children}</div>
                )}
            </div>
        </Card>
    );
};

export default AnalyticsChartCard;
